// Eskom PARCC — the reporting scope layer (Lindiwe, 10 Sep 2026).
//
// She reports on {financial year × quarter}: a per-quarter high-level slide
// (that quarter only) plus a cumulative year-to-date detailed section (every
// quarter up to and including the through-quarter). This module turns a
// ReportScope into the exact row sets each slide is built from, and — because
// production has NO transaction date and (today) NO recorded FY — it surfaces,
// never silently drops, every row it cannot place. Pure, unit-tested in
// test/features/reports/reportScope.test.ts.
import type { Transaction } from '../../data/types';
import { canonicalDivision } from '../../domain/divisions';
import {
  QUARTERS,
  QUARTER_MONTHS,
  type Quarter,
  normaliseFinancialYear,
  normalisePeriod,
  quartersThrough,
  rangeLabel,
  quarterSpanLabel,
  fiscalQuarterOf,
} from '../../domain/fiscal';

export type { Quarter };

/**
 * Canonical division order for EVERY deck table (her slide-3 alphabetical order),
 * so slides that she hand-orders differently are self-consistent here.
 */
export const DECK_DIVISION_ORDER: readonly string[] = [
  'GCD',
  'Gx',
  'ICT & Professional Services',
  'NTCSA',
  'PED',
  'Tactical Sourcing',
];

/** Display-only relabelling (canonical key → deck label); keys stay canonical. */
export const DECK_DIVISION_LABELS: Record<string, string> = { Gx: 'Gx-Head Office' };

/** The deck label for a canonical division (display only). */
export function deckDivisionLabel(canonical: string): string {
  return DECK_DIVISION_LABELS[canonical] ?? canonical;
}

export interface ReportScope {
  /** Canonical financial year, e.g. 'FY27'. */
  fy: string;
  /** Cumulative section runs Q1..throughQuarter; one per-quarter slide per quarter in that span. */
  throughQuarter: Quarter;
  /** Report as-of date, yyyy-mm-dd (drives the "AS OF …" title, not the population). */
  asOfDate: string;
  /** Division filter ('' = all divisions), canonical. */
  division: string;
  /** Include the analytics appendix (off for the Wednesday deck). */
  appendix: boolean;
}

/** Rows the scope could not place, each counted so the readiness strip can surface them. */
export interface SurfacedRows {
  blankYear: Transaction[];
  unrecognisedYear: Transaction[];
  otherFy: Transaction[];
  afterThroughQuarter: Transaction[];
  blankPeriod: Transaction[];
}

export interface ScopedRows {
  inScope: Transaction[];
  surfaced: SurfacedRows;
  /** True once ANY visible row carries a recognised FY (gates whether FY filters). */
  fyRecorded: boolean;
}

/** Does any row carry a recognised financial year? */
export function fyRecordedIn(rows: readonly Transaction[]): boolean {
  return rows.some((r) => normaliseFinancialYear(r.year) !== '');
}

/**
 * Split the caller-visible, non-deleted rows into the in-scope set and the
 * surfaced (excluded-but-counted) classes for a scope. While NO row carries a
 * recognised FY the FY gate is inert (every row passes it) so the deck renders
 * before the backfill; once any row does, a row is in scope only when its FY
 * matches. A row is always placed by its quarter; one it cannot be placed in is
 * surfaced, never silently included.
 */
export function scopeRows(visible: readonly Transaction[], scope: ReportScope): ScopedRows {
  const div = scope.division ? canonicalDivision(scope.division) : '';
  const pool = div ? visible.filter((r) => canonicalDivision(r.division) === div) : visible;
  const fyRecorded = fyRecordedIn(pool);
  const span = quartersThrough(scope.throughQuarter);
  const inScope: Transaction[] = [];
  const surfaced: SurfacedRows = {
    blankYear: [],
    unrecognisedYear: [],
    otherFy: [],
    afterThroughQuarter: [],
    blankPeriod: [],
  };
  for (const row of pool) {
    if (fyRecorded) {
      const rawYear = String(row.year ?? '').trim();
      const rowFy = normaliseFinancialYear(row.year);
      if (rawYear === '') {
        surfaced.blankYear.push(row);
        continue;
      }
      if (rowFy === '') {
        surfaced.unrecognisedYear.push(row);
        continue;
      }
      if (rowFy !== scope.fy) {
        surfaced.otherFy.push(row);
        continue;
      }
    }
    const rowQ = normalisePeriod(row.period);
    if (rowQ === '') {
      surfaced.blankPeriod.push(row);
      continue;
    }
    if (!span.includes(rowQ)) {
      surfaced.afterThroughQuarter.push(row);
      continue;
    }
    inScope.push(row);
  }
  return { inScope, surfaced, fyRecorded };
}

/** In-scope rows restricted to one quarter (for a per-quarter slice). */
export function rowsForQuarter(inScope: readonly Transaction[], quarter: Quarter): Transaction[] {
  return inScope.filter((r) => normalisePeriod(r.period) === quarter);
}

/** The recognised financial years present on the rows, newest first ('FY27' before 'FY26'). */
export function financialYearsPresent(rows: readonly Transaction[]): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const fy = normaliseFinancialYear(r.year);
    if (fy) set.add(fy);
  }
  return [...set].sort((a, b) => Number(b.slice(2)) - Number(a.slice(2)));
}

/** The latest (highest) quarter that has at least one row in `fy` (or across all when FY unrecorded). */
export function maxQuarterWithRows(rows: readonly Transaction[], fy: string, fyRecorded: boolean): Quarter | '' {
  let max = -1;
  for (const r of rows) {
    if (fyRecorded && normaliseFinancialYear(r.year) !== fy) continue;
    const q = normalisePeriod(r.period);
    if (q) max = Math.max(max, QUARTERS.indexOf(q));
  }
  return max >= 0 ? QUARTERS[max] : '';
}

/**
 * The default scope for a fresh Reports page: the latest FY present (else the
 * fiscal year of the as-of date), through the lesser of the fiscal-today quarter
 * and the latest quarter that actually has rows. `division` all, appendix off.
 */
export function defaultScope(rows: readonly Transaction[], asOfDate: string): ReportScope {
  const fyRecorded = fyRecordedIn(rows);
  const present = financialYearsPresent(rows);
  const fiscalNow = fiscalQuarterOf(asOfDate);
  const fy = present[0] ?? fiscalNow.fy;
  const maxQ = maxQuarterWithRows(rows, fy, fyRecorded);
  const fiscalQ = fiscalNow.quarter || 'Q1';
  const fiscalIdx = QUARTERS.indexOf(fiscalQ);
  const maxIdx = maxQ ? QUARTERS.indexOf(maxQ) : fiscalIdx;
  const throughQuarter = QUARTERS[Math.min(fiscalIdx, maxIdx)] ?? 'Q1';
  return { fy, throughQuarter, asOfDate, division: '', appendix: false };
}

/**
 * Quarters in scope that carry NO rows — a readiness ERROR (the deck must never
 * render an empty quarter slide). Excludes Q1 in the degenerate through-Q1 case
 * only when Q1 itself is empty (still an error).
 */
export function emptyQuartersInScope(inScope: readonly Transaction[], scope: ReportScope): Quarter[] {
  const span = quartersThrough(scope.throughQuarter);
  const present = new Set(inScope.map((r) => normalisePeriod(r.period)).filter(Boolean));
  return span.filter((q) => !present.has(q));
}

// ---- Slide titles ---------------------------------------------------------

/** Upper-cased month-range label spanning Q1..throughQuarter (for the cumulative titles). */
function cumRange(scope: ReportScope): string {
  return rangeLabel('Q1', scope.throughQuarter);
}

/**
 * The slide titles for a scope. `quarter` is supplied for a per-quarter slide;
 * `divisionLabel` (upper-cased) for a review-update slide. All en-dashed and
 * upper-cased to match her deck.
 */
export function slideTitles(scope: ReportScope, asOfLong: string) {
  const span = quarterSpanLabel(scope.throughQuarter);
  return {
    cover: `DATE: ${asOfLong.toUpperCase()}`,
    gapsSd: `${scope.fy} ${span}: SUMMARY OF GAPS AND SELF-DECLARATION AS OF ${asOfLong}`,
    divisional: `${scope.fy} ${span} DIVISIONAL SUMMARY AS OF ${asOfLong} (${cumRange(scope)})`,
    bySource: `${scope.fy} ${span} SUMMARY BY SOURCE AS OF ${asOfLong} (${cumRange(scope)})`,
    summaryReport: `${scope.fy} ${span} SUMMARY REPORT AS OF ${asOfLong} (${cumRange(scope)})`,
    reportsToManagement: `REPORTS TO MANAGEMENT: ${span} ${scope.fy} - ${asOfLong}`,
  };
}

/** Per-quarter divisional-summary slide title. */
export function quarterSlideTitle(scope: ReportScope, quarter: Quarter, asOfLong: string): string {
  return `${scope.fy} ${quarter} DIVISIONAL SUMMARY AS OF ${asOfLong} (${QUARTER_MONTHS[quarter].label})`;
}

/** Per-division review-update slide title. */
export function reviewUpdateTitle(scope: ReportScope, canonicalDiv: string, asOfLong: string): string {
  const span = quarterSpanLabel(scope.throughQuarter);
  return `${scope.fy} ${span} - ${deckDivisionLabel(canonicalDiv).toUpperCase()}: REVIEW UPDATE – ${asOfLong}`;
}
